/**
 * html2pdf.js appends a full-screen `div.html2pdf__overlay` to `document.body`
 * to rasterize the cloned document, and only removes it once html2canvas has
 * resolved. When rendering throws, the overlay is leaked: it is `opacity: 0` but
 * `position: fixed` and covers the viewport, so it silently swallows every click
 * on the page. These helpers keep the DOM clean whatever the export outcome.
 *
 * Cleanup is document-wide, so it must not run while another export is still
 * rasterizing — it would tear down that export's live overlay.
 * `beginHtml2PdfExport` / `endHtml2PdfExport` refcount the in-flight exports and
 * sweep only once the last one has settled.
 */

const HTML2PDF_OVERLAY_SELECTOR = '.html2pdf__overlay';

let activeExportCount = 0;

/**
 * Removes any `.html2pdf__overlay` left in the document by a failed export.
 * Safe to call after a successful export — html2pdf has already removed its own.
 *
 * Prefer the `beginHtml2PdfExport` / `endHtml2PdfExport` pair around an export:
 * calling this directly while another export is rasterizing removes its overlay.
 */
export const removeLeakedHtml2PdfOverlays = (): void => {
  const overlays = document.querySelectorAll(HTML2PDF_OVERLAY_SELECTOR);

  for (const overlay of overlays) {
    overlay.remove();
  }
};

/**
 * Registers an export as in flight. Must be paired with `endHtml2PdfExport` in
 * a `finally` block so the count never drifts.
 */
export const beginHtml2PdfExport = (): void => {
  activeExportCount++;
};

/**
 * Marks an export as settled and sweeps leaked overlays once no export remains
 * in flight, so a concurrent export never loses the overlay it is drawing into.
 */
export const endHtml2PdfExport = (): void => {
  activeExportCount = Math.max(0, activeExportCount - 1);

  if (activeExportCount === 0) {
    removeLeakedHtml2PdfOverlays();
  }
};
